import { useEffect, useMemo, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { useMsal } from "@azure/msal-react";
import { useTenant } from "../contexts/tenant-context";
import { FolderExportJobResponse } from "../services/documentService";

interface UseFolderExportSignalROptions {
  onJobUpdated: (job: FolderExportJobResponse) => void;
  onConnectionError?: (message: string) => void;
  /** When false, the hub is not started. Defaults to true for the Export Jobs page. */
  enabled?: boolean;
}

export const useFolderExportSignalR = ({
  onJobUpdated,
  onConnectionError,
  enabled = true,
}: UseFolderExportSignalROptions) => {
  const { instance } = useMsal();
  const { selectedTenantId } = useTenant();

  const connectionRef = useRef<HubConnection | null>(null);
  const onJobUpdatedRef = useRef(onJobUpdated);
  const onConnectionErrorRef = useRef(onConnectionError);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  onJobUpdatedRef.current = onJobUpdated;
  onConnectionErrorRef.current = onConnectionError;

  const shouldConnect = useMemo(() => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || "";
    const signalRScope = process.env.REACT_APP_MSAL_API_SCOPE || "";

    return Boolean(
      enabled
      && selectedTenantId
      && selectedTenantId !== "__GLOBAL_ADMIN__"
      && apiBaseUrl
      && signalRScope
    );
  }, [enabled, selectedTenantId]);

  useEffect(() => {
    if (!shouldConnect) {
      setConnectionState(HubConnectionState.Disconnected);
      return;
    }

    let isCancelled = false;

    const connect = async () => {
      const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!activeAccount) {
        onConnectionErrorRef.current?.(
          "Realtime updates are unavailable until authentication is ready."
        );
        return;
      }

      const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      const tenantQuery = encodeURIComponent(selectedTenantId!);
      const hubUrl = `${baseUrl}/hubs/folder-export-jobs?tenantId=${tenantQuery}`;

      const connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: async () => {
            const token = await instance.acquireTokenSilent({
              account: activeAccount,
              scopes: [process.env.REACT_APP_MSAL_API_SCOPE || ""],
            });

            return token.accessToken;
          },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .configureLogging(LogLevel.Warning)
        .build();

      connectionRef.current = connection;

      connection.on("folderExportJobUpdated", (job: FolderExportJobResponse) => {
        onJobUpdatedRef.current(job);
      });

      connection.onclose(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Disconnected);
        }
      });

      connection.onreconnecting(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Reconnecting);
        }
      });

      connection.onreconnected(() => {
        if (!isCancelled) {
          setConnectionState(HubConnectionState.Connected);
        }
      });

      try {
        await connection.start();
        if (isCancelled) {
          await connection.stop();
          return;
        }

        setConnectionState(HubConnectionState.Connected);
      } catch (error) {
        if (!isCancelled) {
          onConnectionErrorRef.current?.(
            `Realtime connection failed: ${(error as Error).message}`
          );
          setConnectionState(HubConnectionState.Disconnected);
        }
      }
    };

    connect();

    return () => {
      isCancelled = true;
      const current = connectionRef.current;
      connectionRef.current = null;
      if (current) {
        current.stop().catch(() => undefined);
      }
    };
  }, [instance, selectedTenantId, shouldConnect]);

  return {
    isConnected: connectionState === HubConnectionState.Connected,
    connectionState,
  };
};
